import type { ButtonItem, StrapiImage } from '@/types/homepage';
import { resolveStrapiMediaUrl, getStrapiMediaUrl } from '@/lib/strapi-media-url';

type MaybeString = string | null | undefined;

/**
 * Resolve a Strapi image to an absolute URL. Delegates to the canonical
 * resolver in `@/lib/strapi-media-url` so behavior stays consistent everywhere.
 * Returns `null` when the image is missing.
 */
export function getImageUrl(image: StrapiImage | null | undefined): string | null {
  return resolveStrapiMediaUrl(image?.url);
}

/** Resolve a Strapi media URL from the original CMS upload. */
export function getImageUrlForVariant(
  image: StrapiImage | null | undefined,
  variant: 'thumbnail' | 'small' | 'medium' | 'large',
): string | null {
  return getStrapiMediaUrl(image, variant);
}

/**
 * Strip HTML tags and collapse whitespace. Used wherever the API ships
 * `<p><span>...</span></p>`-wrapped plain text but the UI needs a string
 * (e.g. animation that splits on whitespace).
 */
const NAMED_HTML_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  hellip: '…',
  mdash: '—',
  ndash: '–',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
};

function decodeHtmlEntities(input: string): string {
  return input
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&([a-zA-Z]+);/g, (match, name) => NAMED_HTML_ENTITIES[name] ?? match);
}

export function stripHtml(input: MaybeString): string {
  return decodeHtmlEntities(input ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Convert a CMS `buttonUrl` into a useable href.
 *
 * - `null` / empty / `'#!'` → returns `null` (caller renders as a `<button>`).
 * - Hash-only links (`'#anything'`) → returned as-is.
 * - Absolute URLs (`http`, `https`, `mailto:`, `tel:`) → returned as-is.
 * - Relative paths → prefixed with `/{locale}` and a leading slash.
 * - Leading whitespace is trimmed (CMS sometimes ships ` /landmark-projects`).
 */
export function localizeUrl(href: MaybeString, locale: string): string | null {
  if (!href) return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed === '#!') return null;
  if (trimmed.startsWith('#')) return trimmed;
  if (/^(https?:|mailto:|tel:)/.test(trimmed)) return trimmed;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `/${locale}${path}`;
}

/**
 * Pick a button by `buttonType` from a `ButtonItem[]`. Falls back to the
 * first item when the requested type is absent. Returns `undefined` for
 * empty arrays.
 */
export function pickButton(
  arr: ButtonItem[] | undefined,
  type: 'Solid' | 'Transparent',
): ButtonItem | undefined {
  if (!arr || arr.length === 0) return undefined;
  return arr.find((b) => b.buttonType === type) ?? arr[0];
}

/**
 * Extract bullet text from an HTML string. Looks for `<li>...</li>` items;
 * if none are found, returns the entire stripped string as a single bullet.
 */
export function extractListItems(html: MaybeString): string[] {
  const source = html ?? '';
  // CMS produces flat lists only; nested <li> content would be merged into a single bullet.
  const matches = source.match(/<li[^>]*>([\s\S]*?)<\/li>/g);
  if (matches && matches.length > 0) {
    return matches.map((m) => stripHtml(m)).filter(Boolean);
  }
  const single = stripHtml(source);
  return single ? [single] : [];
}

/**
 * Format a stat value + suffix.
 *
 * - No suffix: just the value.
 * - Suffix starts with a letter: insert a single space (`"13.8 MTPA"`).
 * - Suffix starts with a symbol/digit: concatenate (`"5000+"`, `"7"`).
 */
export function formatStatValue(value: string, suffix: MaybeString): string {
  const s = (suffix ?? '').trim();
  if (!s) return value;
  return /^[A-Za-z]/.test(s) ? `${value} ${s}` : `${value}${s}`;
}
